import React, { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Loader2, Pause, Play, Plus, Trash2, Zap } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { AaiRoutine } from '../../types/aai';
import ScheduleSkillDialog from './ScheduleSkillDialog';

/**
 * F1. Routines, in Settings, which is where Josh said they should live.
 *
 * A routine used to be a label on a skill: accepting a "routine" suggestion in
 * My Tools created an ordinary skill with the word attached and nothing else. No
 * schedule existed anywhere in the product, so nothing ever recurred.
 *
 * This screen shows the three things a routine now has, and in the order a
 * person cares about them: when it next runs, when it last ran, and a switch to
 * stop it. Pause matters more than edit in version one, because the reason
 * somebody opens this page is that something is firing when it should not.
 */

const CADENCE_LABEL: Record<string, string> = {
  daily: 'Every day',
  weekdays: 'Every weekday',
  weekly: 'Every week',
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function describe(r: AaiRoutine): string {
  const base = CADENCE_LABEL[r.cadence] || r.cadence;
  const day = r.cadence === 'weekly' && r.weekday !== null ? ` on ${DAYS[r.weekday]}` : '';
  return `${base}${day} at ${r.time_of_day}`;
}

/** A timestamp as a person reads it, not as the API stores it. */
function when(iso: string | null): string {
  if (!iso) return 'not scheduled';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'not scheduled';
  return d.toLocaleString(undefined, {
    weekday: 'short', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short',
  });
}

const OUTCOME_STYLE: Record<string, string> = {
  worked: 'bg-emerald-50 text-emerald-700',
  partial: 'bg-amber-50 text-amber-700',
  failed: 'bg-red-50 text-red-700',
};

/**
 * "Worked" is a person's word. A timer can report that a run produced something
 * or that it did not; it cannot report that the output was any good, and nobody
 * has looked at it yet. So a routine's own runs are labelled by what actually
 * happened, and the outcome vocabulary stays where a person put it.
 */
function outcomeLabel(outcome: string, source: string): string {
  if (source !== 'routine') return outcome;
  return outcome === 'failed' ? 'did not run' : 'ran';
}

const RoutinesTab: React.FC = () => {
  const [routines, setRoutines] = useState<AaiRoutine[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // F1: making a routine used to be possible only by accepting a tool-scout
  // suggestion that happened to come back as kind "routine". This screen can
  // make one directly, which is where a person looks for it anyway.
  const [scheduling, setScheduling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await aaiApi.listRoutines();
      setRoutines(data.routines);
    } catch {
      toast.error('Could not load your routines');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id: string, fn: () => Promise<unknown>, failure: string) => {
    setBusy(id);
    try {
      await fn();
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || failure);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-panel border border-line p-6 flex items-center gap-2 text-sm text-ink-soft">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your routines...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-panel border border-line p-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[20px] font-bold leading-[28px] tracking-[-0.005em] text-ink mb-1">Routines</h2>
          <p className="text-sm text-ink-dim">
            Skills that run on their own, so you do not have to remember to.
          </p>
        </div>
        <button onClick={() => setScheduling(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-brand-600 whitespace-nowrap">
          <Plus size={14} /> Schedule a skill
        </button>
      </div>

      {routines.length === 0 && (
        <div className="rounded-lg border border-dashed border-line px-5 py-8 text-center">
          <CalendarClock className="w-5 h-5 text-ink-soft mx-auto mb-2" />
          <p className="text-sm text-ink-dim">Nothing runs on a schedule yet.</p>
          <button onClick={() => setScheduling(true)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-brand-600 whitespace-nowrap">
            <Plus size={14} /> Schedule a skill
          </button>
        </div>
      )}

      <div className="space-y-2">
        {routines.map((r) => (
          <div key={r.id} className={`rounded-lg border border-line-soft px-4 py-3 ${r.paused ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink">{r.skill_name}</div>
                <div className="text-[12.5px] text-ink-dim">{describe(r)}</div>
                <div className="text-[11.5px] text-ink-soft mt-0.5">
                  {r.paused
                    ? 'Paused'
                    : <>Next run {when(r.next_run_at)}</>}
                  {r.last_run_at && <> &middot; last ran {when(r.last_run_at)}</>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => act(r.id, () => aaiApi.runRoutineNow(r.id), 'Could not run it')}
                  disabled={busy === r.id}
                  title="Run it now, without waiting for its time"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-canvas px-2.5 py-1.5 text-[12px] font-semibold text-ink hover:bg-canvas-inset disabled:opacity-50 whitespace-nowrap"
                >
                  <Zap size={13} /> Run now
                </button>
                <button
                  onClick={() => act(r.id, () => aaiApi.pauseRoutine(r.id, !r.paused), 'Could not change that')}
                  disabled={busy === r.id}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-canvas px-2.5 py-1.5 text-[12px] font-semibold text-ink hover:bg-canvas-inset disabled:opacity-50 whitespace-nowrap"
                >
                  {r.paused ? <><Play size={13} /> Resume</> : <><Pause size={13} /> Pause</>}
                </button>
                <button
                  onClick={() => act(r.id, () => aaiApi.deleteRoutine(r.id), 'Could not remove it')}
                  disabled={busy === r.id}
                  title="Stop running this on a schedule. The skill itself stays."
                  className="rounded-lg p-1.5 text-ink-soft hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {r.runs.length > 0 && (
              <div className="mt-2.5 border-t border-gray-50 pt-2">
                <button
                  onClick={() => setOpen(open === r.id ? null : r.id)}
                  className="text-[11.5px] font-semibold text-ink-dim hover:text-ink"
                >
                  {open === r.id ? 'Hide' : 'Show'} the last {r.runs.length} run{r.runs.length === 1 ? '' : 's'}
                </button>
                {open === r.id && (
                  <div className="mt-2 space-y-1.5">
                    {r.runs.map((run, i) => (
                      <div key={i} className="rounded-lg bg-canvas px-3 py-2">
                        <div className="flex items-center gap-2 text-[11px]">
                          <span className={`rounded-full px-2 py-0.5 font-semibold ${OUTCOME_STYLE[run.outcome] || 'bg-canvas-inset text-ink-muted'}`}>
                            {outcomeLabel(run.outcome, run.source)}
                          </span>
                          <span className="text-ink-soft">{when(run.ran_at)}</span>
                          {/* A timer's run and a person's run both belong in the
                              history, but they are not the same evidence: only a
                              person's counts toward a skill graduating. */}
                          {run.source === 'routine' && <span className="text-ink-soft">on its own</span>}
                          {run.source !== 'routine' && <span className="text-ink-soft">you ran this</span>}
                        </div>
                        {run.note && (
                          <p className="mt-1 whitespace-pre-wrap text-[12px] text-ink-muted line-clamp-6">{run.note}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {scheduling && (
        <ScheduleSkillDialog onClose={() => setScheduling(false)} onSaved={load} />
      )}
    </div>
  );
};

export default RoutinesTab;
